/**
 * Faturas de UM pagamento — o drill-in da tela de Pagamentos.
 *
 * É esta lista que responde ao estado da linha: são as faturas com `paid_by` deste pagamento. Se
 * a soma dos valores recebidos fecha com o pagamento e todas estão pagas, ele está IDENTIFICADO;
 * fechando com faturas abertas, SINALIZADO; qualquer outra coisa, PENDENTE.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FaCommentDots, FaInfoCircle, FaTimes } from 'react-icons/fa';

import BadgeStatus from '../../../atoms/BadgeStatus';
import AddInvoiceCommentModal from './AddInvoiceCommentModal';
import InvoiceDetailsModal from './InvoiceDetailsModal';
import ApproveInvoicesDialog from './ApproveInvoicesDialog';
import UnmarkInvoicesDialog from './UnmarkInvoicesDialog';
import CommentCell from '../../../molecules/CommentCell';
import PartyCell from '../../../molecules/PartyCell';
import { nominalAmountOf, openAmountOf, openAmountValue } from '../services/invoiceAmounts';
import { PaymentRow, listInvoicesByPayment } from '../services/billingApi';
import { formatBRL } from '../../../../utils/currencyUtils';
import { formatDateBR } from '../../../../utils/DateUtils';
import { STATUS_META } from '../PaymentsPanel';
import { modeOf } from '../constants/liquidationModes';

/* Coluna OCULTA, não removida: o `hidden` tira da VISTA e mantém no HTML, que é o que a tela de
   Faturas já faz com `#` e `Fatura`. O UUID encurtado não ajuda quem confere um pagamento, mas é
   por ele que se acha a linha no banco quando algo não bate. */
const OCULTA = 'hidden';

const ACTION_BUTTON = 'p-1.5 rounded hover:bg-gray-100 dark:hover:bg-slate-600 disabled:opacity-30 disabled:cursor-not-allowed';

const isSignalled = (inv: any): boolean => inv.paymentSignalled === true;
const isEffected = (inv: any): boolean => inv.status === 'PAID' && !isSignalled(inv);

/**
 * Acréscimo (+) ou desconto (−) da linha.
 *
 * Efetivada: o valor congelado na efetivação (`adjustmentAmount`, nulo quando a liquidação não foi
 * total). Sinalizada: a conta que a efetivação vai fazer, recebido menos o "Em Aberto" do título.
 */
export const adjustmentOf = (inv: any): number | null => {
    if (isSignalled(inv)) {
        const open = openAmountValue(inv);
        return open == null ? null : Math.round((Number(inv.amountPaid || 0) - open) * 100) / 100;
    }
    if (isEffected(inv)) return inv.adjustmentAmount == null ? null : Number(inv.adjustmentAmount);
    return null;
};

/** Cor só em pagamento TOTAL: no parcial a diferença para o saldo não é desconto, é o que falta. */
export const adjustmentClass = (inv: any, adjustment: number | null): string => {
    if (modeOf(inv.totalPayment) !== 'TOTAL' || adjustment == null) return '';
    if (adjustment <= -0.005) return 'text-red-600';
    if (adjustment >= 0.005) return 'text-blue-600';
    return '';
};

const TOTAL_PARTIAL_BADGE = 'inline-block min-w-[1.25rem] px-1.5 py-0.5 rounded text-[10px] font-semibold text-white';

export const totalPartialBadge = (totalPayment: boolean | null | undefined) => {
    const mode = modeOf(totalPayment);
    if (mode === 'TOTAL') return { text: 'T', color: 'bg-green-500', title: 'Pagamento total' };
    if (mode === 'PARTIAL') return { text: 'P', color: 'bg-pay-partial', title: 'Pagamento parcial' };
    return { text: 'A', color: 'bg-[#7c3aed]', title: 'Amortização de parcelas' };
};

const sumPaid = (list: any[]) => list.reduce((sum, inv) => sum + Number(inv.amountPaid || 0), 0);

type BadgeColor = 'blue' | 'green' | 'red' | 'orange' | 'gray' | 'gold' | 'olive' | 'teal';

const INVOICE_STATUS: Record<string, { label: string; color: BadgeColor }> = {
    OPEN: { label: 'Em aberto', color: 'gold' },
    PAID: { label: 'Paga', color: 'green' },
    CANCELLED: { label: 'Cancelada', color: 'red' },
};

/**
 * O que a linha DIZ quando a fatura está marcada neste pagamento.
 *
 * ⚠️ O status cru mente aqui. Esta é a lista de faturas de UM pagamento, e enquanto ele não for
 * efetivado todas elas estão apenas MARCADAS — dizer "Em aberto" esconde isso, e dizer "Cancelada"
 * é pior ainda: a tela de Identificação marca sempre a fatura ORIGINAL do título, que pode ter sido
 * cancelada numa reemissão, e o usuário fica sem entender onde a marcação dele foi parar.
 *
 * `yellow` é o `bg-yellow-500` — o amarelo de "tem dinheiro apontado, falta aprovar".
 */
const SIGNALLED = { label: 'Pagto Sinalizado', color: 'yellow' as BadgeColor };

interface Props {
    payment: PaymentRow;
    onClose: () => void;
}

interface ModalProps extends Props {
    /** avisa a tela de trás para ela recarregar o dia — aprovar/reprovar aqui muda a linha de lá */
    onChanged?: () => void;
}

const PaymentInvoicesModal: React.FC<ModalProps> = ({ payment, onClose, onChanged }) => {
    const [rows, setRows] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [approving, setApproving] = useState(false);
    const [commenting, setCommenting] = useState<any>(null);
    const [inspecting, setInspecting] = useState<any>(null);
    const [rejecting, setRejecting] = useState(false);
    const [selected, setSelected] = useState<Set<string>>(new Set());

    const reload = useCallback(async () => {
        try {
            setRows(await listInvoicesByPayment(payment.paymentId));
        } catch { setRows([]); }
        setLoading(false);
    }, [payment.paymentId]);

    useEffect(() => { reload(); }, [reload]);

    const selectable = useMemo(() => rows.filter(isSignalled), [rows]);
    const selectedRows = useMemo(
        () => selectable.filter(inv => selected.has(inv.invoiceId)), [selectable, selected]);
    const allSelected = selectable.length > 0 && selectedRows.length === selectable.length;
    const someSelected = selectedRows.length > 0 && !allSelected;

    const toggle = (invoiceId: string) => setSelected(current => {
        const next = new Set(current);
        if (next.has(invoiceId)) next.delete(invoiceId); else next.add(invoiceId);
        return next;
    });
    const toggleAll = (checked: boolean) =>
        setSelected(checked ? new Set(selectable.map(inv => inv.invoiceId)) : new Set());

    const afterAction = async () => {
        setSelected(new Set());
        await reload();
        onChanged?.();
    };

    const total = Number(payment.amount || 0);
    const signalledTotal = sumPaid(selectable);
    const effectedTotal = sumPaid(rows.filter(isEffected));
    const selectedTotal = sumPaid(selectedRows);
    const missing = total - signalledTotal - effectedTotal;
    const nothingSelected = selectedRows.length === 0;
    const NOTHING_SELECTED = 'Selecione ao menos uma fatura sinalizada';

    return (
        <>
            {/* ⚠️ O overlay fecha no clique, e por isso os modais FILHOS não moram dentro dele.
            Dentro, qualquer clique neles — inclusive focar o campo de texto — borbulhava até este
            `onClick` e fechava os dois de uma vez. Irmãos no mesmo fragmento, o clique do filho
            nunca passa por aqui. */}
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
                <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-[96rem] max-h-[90vh] flex flex-col"
                    onClick={e => e.stopPropagation()}>
                    <div className="flex items-center justify-between px-6 py-4 border-b dark:border-slate-600">
                        <div>
                            <h3 className="text-lg font-semibold">Faturas do pagamento</h3>
                            <p className="text-xs text-gray-500 mt-0.5">
                                #{payment.paymentId.slice(0, 8)}
                                {' · '}{STATUS_META[payment.status].label}
                                {' · '}valor {formatBRL(payment.amount || 0)}
                            </p>
                        </div>
                        <button onClick={onClose} aria-label="Fechar"
                            className="text-gray-400 hover:text-gray-600"><FaTimes /></button>
                    </div>

                    <div className="px-6 py-4 overflow-y-auto">
                        {loading ? (
                            <p className="text-center text-gray-500 py-6">Carregando...</p>
                        ) : rows.length === 0 ? (
                            <p className="text-center text-gray-500 py-6">
                                Nenhuma fatura sinalizada neste pagamento ainda.
                            </p>
                        ) : (
                            <>
                                <table className="w-full text-sm">
                                    <thead className="bg-gray-100 dark:bg-slate-700">
                                        <tr className="text-left text-xs text-gray-500 dark:text-gray-400 uppercase">
                                            {/* OCULTA, não removida: o UUID encurtado não diz nada
                                            a quem confere um pagamento e roubava a largura das
                                            colunas de parte, mas quem depura ainda o acha no HTML
                                            para localizar a linha no banco. Mesmo `hidden` das
                                            colunas `#` e `Fatura` da tela de Faturas. */}
                                            <th className="px-2 py-2 w-8">
                                                <input type="checkbox" className="rounded"
                                                    aria-label="Selecionar todas as faturas sinalizadas"
                                                    checked={allSelected}
                                                    disabled={selectable.length === 0}
                                                    ref={el => { if (el) el.indeterminate = someSelected; }}
                                                    onChange={e => toggleAll(e.target.checked)} />
                                            </th>
                                            <th className={`${OCULTA} px-2 py-2`}>Fatura</th>
                                            <th className="px-2 py-2">Contrato</th>
                                            {/* a OPERAÇÃO ao lado do CONTRATO, como nas telas */}
                                            <th className="px-2 py-2 whitespace-nowrap">Operação</th>
                                            {/* Quem ORIGINOU a dívida e quem a DEVE, nesta ordem e
                                            logo depois do contrato: é o bloco que identifica a
                                            dívida, antes dos números. Mesma ordem das telas de
                                            Títulos e de Faturas. */}
                                            {/* sem quebra: a coluna é estreita e o título de três
                                            palavras virava três linhas enquanto a lista carrega */}
                                            <th className="px-2 py-2 whitespace-nowrap max-w-[160px]">Emitente / Cedente</th>
                                            <th className="px-2 py-2 max-w-[160px]">Devedor</th>
                                            <th className="px-2 py-2">Título/NF</th>
                                            {/* Três valores lado a lado, do que foi cobrado ao que
                                            falta: o nominal da fatura, o que este pagamento aponta
                                            para ela, e o saldo do TÍTULO. "Valor"/"Recebido" eram
                                            ambíguos — o primeiro não dizia de quê, e o segundo
                                            afirmava recebimento numa fatura que pode estar só
                                            sinalizada. */}
                                            <th className="px-2 py-2 text-right whitespace-nowrap">Valor Nominal</th>
                                            <th className="px-2 py-2 text-right whitespace-nowrap">Em Aberto</th>
                                            <th className="px-2 py-2 text-right whitespace-nowrap">Recebido</th>
                                            <th className="px-2 py-2 text-right whitespace-nowrap"
                                                title="Acréscimos / Descontos">Acrs./Desc.</th>
                                            <th className="px-2 py-2 text-center">Vencimento</th>
                                            <th className="px-2 py-2 text-center" title="Total / Parcial">T/P</th>
                                            <th className="px-2 py-2 text-center">Status</th>
                                            {/* entre Status e Ações, como na tela de Faturas */}
                                            <th className="px-2 py-2">Comentários</th>
                                            <th className="px-2 py-2 text-center">Ações</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((inv: any) => {
                                            // marcada vence o status cru: PAGA nunca chega aqui
                                            // marcada (a efetivação apaga a marcação)
                                            const st = inv.paymentSignalled ? SIGNALLED
                                                : INVOICE_STATUS[inv.status]
                                                || { label: inv.status, color: 'gray' as BadgeColor };
                                            const shortId = String(inv.invoiceId).slice(0, 8);
                                            const adjustment = adjustmentOf(inv);
                                            const badge = totalPartialBadge(inv.totalPayment);
                                            return (
                                                <tr key={inv.invoiceId} className="border-t dark:border-slate-600">
                                                    <td className="px-2 py-2">
                                                        {isSignalled(inv) ? (
                                                            <input type="checkbox" className="rounded"
                                                                aria-label={`Selecionar fatura ${shortId}`}
                                                                checked={selected.has(inv.invoiceId)}
                                                                onChange={() => toggle(inv.invoiceId)} />
                                                        ) : (
                                                            <input type="checkbox" className="rounded" disabled
                                                                aria-label={`Fatura ${shortId} não selecionável`}
                                                                title={isEffected(inv) ? 'Já efetivada' : 'Não sinalizada neste pagamento'} />
                                                        )}
                                                    </td>
                                                    <td className={`${OCULTA} px-2 py-2 text-xs font-mono text-gray-500`} title={inv.invoiceId}>
                                                        {String(inv.invoiceId).slice(0, 8)}
                                                    </td>
                                                    <td className="px-2 py-2 text-right">{inv.contractLabel || '-'}</td>
                                                    <td className="px-2 py-2 truncate max-w-[140px]">{inv.operationNumber || '-'}</td>
                                                    <td className="px-2 py-2 max-w-[160px]">
                                                        <PartyCell name={inv.issuer?.name}
                                                            documentNumber={inv.issuer?.documentNumber}
                                                            maxWidthClass="max-w-[160px]" />
                                                    </td>
                                                    <td className="px-2 py-2 max-w-[160px]">
                                                        <PartyCell name={inv.payer?.name}
                                                            documentNumber={inv.payer?.documentNumber}
                                                            maxWidthClass="max-w-[160px]" />
                                                    </td>
                                                    <td className="px-2 py-2">{inv.details?.documentTitle || '-'}</td>
                                                    <td className="px-2 py-2 text-right">{nominalAmountOf(inv)}</td>
                                                    <td className="px-2 py-2 text-right">{openAmountOf(inv)}</td>
                                                    {/* A cor diz em que passo o dinheiro está, sem
                                                    obrigar a cruzar com a coluna de Status: âmbar é
                                                    promessa (sinalizado, esperando aprovação),
                                                    verde é fato (efetivado). É a MESMA convenção da
                                                    linha da tela de Pagamentos e das fatias da
                                                    barra do passo 3. */}
                                                    <td className={`px-2 py-2 text-right font-medium ${inv.paymentSignalled
                                                        ? 'text-yellow-600'
                                                        : inv.status === 'PAID' ? 'text-green-600' : ''}`}>
                                                        {formatBRL(inv.amountPaid || 0)}
                                                    </td>
                                                    <td className={`px-2 py-2 text-right whitespace-nowrap ${adjustmentClass(inv, adjustment)}`}>
                                                        {formatBRL(adjustment)}
                                                    </td>
                                                    <td className="px-2 py-2 text-center max-w-[110px]">{formatDateBR(inv.dueDate)}</td>
                                                    <td className="px-2 py-2 text-center">
                                                        <span className={`${TOTAL_PARTIAL_BADGE} ${badge.color}`} title={badge.title}>
                                                            {badge.text}
                                                        </span>
                                                    </td>
                                                    <td className="px-2 py-2 text-center whitespace-nowrap max-w-[140px]">
                                                        <BadgeStatus text={st.label} color={st.color} />
                                                    </td>
                                                    <td className="px-2 py-2 max-w-[220px] truncate">
                                                        <CommentCell notes={inv.details?.notesList || []} />
                                                    </td>
                                                    <td className="px-2 py-2 text-center whitespace-nowrap">
                                                        {/* comentar vale em qualquer estado */}
                                                        <button
                                                            onClick={() => setCommenting(inv)}
                                                            aria-label={`Adicionar comentário à fatura ${shortId}`}
                                                            title="Adicionar comentário"
                                                            className={`${ACTION_BUTTON} text-blue-600 hover:text-blue-700`}>
                                                            <FaCommentDots />
                                                        </button>
                                                        <button
                                                            onClick={() => setInspecting(inv)}
                                                            aria-label={`Informações da fatura ${shortId}`}
                                                            title={isSignalled(inv) ? 'Informações da sinalização' : 'Informações da fatura'}
                                                            className={`${ACTION_BUTTON} text-slate-500 hover:text-slate-700 dark:text-slate-300`}>
                                                            <FaInfoCircle />
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>

                            </>
                        )}
                    </div>

                    {/* a conta é o que decide o estado do pagamento — mostrá-la evita que o usuário
                    tenha de somar a coluna com o olho para entender por que a linha ainda está
                    pendente */}
                    {!loading && rows.length > 0 && (
                        <div className="flex items-center justify-between gap-4 px-6 py-3 border-t dark:border-slate-600">
                            <p className="text-sm text-gray-700 dark:text-gray-200">
                                Sinalizado: <strong>{formatBRL(signalledTotal)}</strong>, Efetivado:{' '}
                                <strong>{formatBRL(effectedTotal)}</strong> de <strong>{formatBRL(total)}</strong>
                                {missing >= 0.005 && <> — faltam {formatBRL(missing)}</>}
                                {!nothingSelected && (
                                    <> — Selecionado <span className="text-blue-600">{formatBRL(selectedTotal)}</span></>
                                )}
                                .
                            </p>
                            <div className="flex gap-2 shrink-0">
                                <button onClick={() => setApproving(true)} disabled={nothingSelected}
                                    title={nothingSelected ? NOTHING_SELECTED : 'Efetiva as faturas selecionadas'}
                                    className="px-4 py-2 text-sm rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed">
                                    Aprovar
                                </button>
                                <button onClick={() => setRejecting(true)} disabled={nothingSelected}
                                    title={nothingSelected ? NOTHING_SELECTED : 'Remove a sinalização das faturas selecionadas'}
                                    className="px-4 py-2 text-sm rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed">
                                    Reprovar
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {commenting && (
                <AddInvoiceCommentModal
                    invoice={commenting}
                    onClose={() => setCommenting(null)}
                    onAdded={reload}
                />
            )}
            {inspecting && (
                <InvoiceDetailsModal invoice={inspecting} onClose={() => setInspecting(null)} />
            )}
            {approving && (
                <ApproveInvoicesDialog
                    payment={payment}
                    invoices={selectedRows}
                    onClose={() => setApproving(false)}
                    onApproved={afterAction}
                />
            )}
            {rejecting && (
                <UnmarkInvoicesDialog
                    payment={payment}
                    invoices={selectedRows}
                    onClose={() => setRejecting(false)}
                    onUnmarked={afterAction}
                />
            )}
        </>
    );
};

export default PaymentInvoicesModal;
